import { Link } from "react-router-dom";
import { ShieldCheck, FileCheck, Radio, Clock, MapPin, Mail, ChevronRight, BadgeCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, CtaBand, Breadcrumbs } from "@/components/Blocks";
import { CITIES, EMAIL, EMAIL_HREF, PHONE, IMAGES } from "@/data/contentEngine";

const ROLES = [
  {
    title: "Static Security Officers",
    text: "Manned guarding across retail, corporate, industrial and construction sites throughout Northern Ireland. Day, night and weekend shifts available.",
  },
  {
    title: "Door Supervisors",
    text: "SIA Door Supervisor licence holders for pubs, clubs, hotels and late-night venues from Belfast's Cathedral Quarter to Enniskillen's hospitality quarter.",
  },
  {
    title: "Mobile Patrol & Response Officers",
    text: "Drivers with patrol experience for randomised site inspections, key holding and rapid alarm response across the regional patrol network.",
  },
  {
    title: "CCTV Monitoring Operators",
    text: "SIA CCTV licence holders for our 24/7 monitoring centre — alarm verification, audio challenge and dispatch coordination.",
  },
  {
    title: "Event Stewards & Security",
    text: "Festival, concert and sporting-event staff for the NI events calendar — stewarding, crowd management and licensed positions.",
  },
  {
    title: "Supervisors & Contract Managers",
    text: "Experienced security professionals to lead teams, run client sites and manage multi-site contracts across the region.",
  },
];

const REQUIREMENTS = [
  { icon: ShieldCheck, title: "Valid SIA Licence", text: "A current Security Industry Authority licence for the role you are applying for — frontline roles cannot start without one." },
  { icon: FileCheck, title: "BS7858 Screening", text: "All applicants complete BS7858 vetting: five-year employment history, identity verification and right-to-work checks." },
  { icon: Radio, title: "Communication Standards", text: "Clear written and spoken English for radio work, incident reports and professional client contact." },
  { icon: Clock, title: "Flexibility", text: "Security runs around the clock — days, nights and weekends. Full-time, part-time and casual shifts are available." },
];

const Careers = () => (
  <>
    <Seo
      title="Security Jobs Northern Ireland | SIA Officer Careers | Xtreme Protection"
      description="Security jobs across Northern Ireland: SIA security officers, door supervisors, CCTV operators, patrol drivers & event stewards. Apply by emailing admin@xtremeprotection.uk."
      path="/careers"
    />
    <PageHero
      eyebrow="Careers at Xtreme Protection"
      title="Join Northern Ireland's security team"
      sub="We recruit SIA-licensed officers, door supervisors, CCTV operators and stewards across all six counties — with proper vetting, real supervision and a 24/7 control room behind every shift."
      image={IMAGES.officer}
      testId="careers-hero"
    />
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="careers-content">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Careers", to: null }]} />

      <div className="mt-12">
        <SectionHead
          eyebrow="Open Roles"
          title="Positions we recruit for"
          sub="New contracts across Northern Ireland mean we are always interested in hearing from licensed, reliable people."
          testId="careers-roles-heading"
        />
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {ROLES.map((r, i) => (
            <Reveal key={r.title} delay={(i % 3) * 0.08}>
              <div className="tactical-card h-full rounded-md p-6" data-testid={`careers-role-${i}`}>
                <BadgeCheck className="h-6 w-6 text-amber-500" />
                <h2 className="mt-4 font-display text-xl font-bold uppercase tracking-wide text-slate-100">
                  {r.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-16">
        <SectionHead
          eyebrow="Before You Apply"
          title="What you will need"
          testId="careers-requirements-heading"
        />
        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {REQUIREMENTS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <div className="flex gap-4" data-testid={`careers-requirement-${i}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-amber-500/40 bg-amber-500/10">
                  <r.icon className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-wide text-slate-100">
                    {r.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-400">{r.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-16">
        <SectionHead
          eyebrow="Where We Recruit"
          title="Work in your own area"
          sub="Officers are deployed close to home wherever possible — we recruit in every city and town we cover."
          testId="careers-locations-heading"
        />
        <div className="mt-8 flex flex-wrap gap-2" data-testid="careers-locations-list">
          {CITIES.map((c) => (
            <Link
              key={c.slug}
              to={`/cities/${c.slug}`}
              data-testid={`careers-location-${c.slug}`}
              className="flex items-center gap-1.5 rounded-sm border border-slate-700 bg-obsidian-900 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-400"
            >
              <MapPin className="h-3 w-3 text-amber-500" /> {c.name}
            </Link>
          ))}
        </div>
      </div>

      <Reveal delay={0.1}>
        <div className="mt-16 rounded-md border border-amber-500/30 bg-obsidian-900 p-8 text-center" data-testid="careers-apply-panel">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-slate-100">
            How to apply
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
            Email your CV and your SIA licence number to{" "}
            <a href={EMAIL_HREF} className="text-amber-400 hover:underline" data-testid="careers-apply-email">
              {EMAIL}
            </a>{" "}
            with the subject line "Security Officer Application — [Your Town]". Tell us which roles and
            locations suit you, and our recruitment team will be in touch. You can also call {PHONE} to
            ask about current vacancies.
          </p>
          <a
            href={`${EMAIL_HREF}?subject=${encodeURIComponent("Security Officer Application")}`}
            data-testid="careers-apply-button"
            className="mt-6 inline-flex items-center gap-2 rounded-sm bg-amber-500 px-8 py-3.5 font-display text-base font-bold uppercase tracking-[0.15em] text-obsidian-950 transition-all duration-300 hover:bg-amber-400 active:scale-95"
          >
            <Mail className="h-4 w-4" /> Email Your Application
          </a>
        </div>
      </Reveal>
    </section>
  </>
);

export default Careers;
